/**
 * EventFormInitializer
 *
 * Creates initial participant lists for create and edit modes.
 * Extracted from use-event-form-state for single responsibility.
 */

import type { EventParticipant } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { EventParticipantsManager } from './EventParticipantsManager';
import { FamilyMemberCalendarResolver } from '@/lib/family/FamilyMemberCalendarResolver';

type CreateModeParticipantOptions = {
  ownerEmail: string | undefined;
  ownerName: string | null | undefined;
  userId: string | undefined;
  avatarUrl: string | undefined;
  calendarId: string | null;
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
};

type EditModeParticipantOptions = {
  participants: EventParticipant[];
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  calendars: DeviceCalendar[];
  meMemberId?: string;
};

export class EventFormInitializer {
  /**
   * Check if a calendar is linked to the "Me" family member.
   * Reusable by form hook and UI components.
   */
  static isCalendarLinkedToMe(
    calendarId: string | null,
    familyMembers: FamilyMember[],
    calendarMappings: CalendarMemberMapping[],
  ): boolean {
    if (!calendarId) return false;
    const meMember = familyMembers.find(fm => fm.isMe);
    if (!meMember) return false;
    return calendarMappings.some(
      m => m.calendarId === calendarId && m.memberIds.includes(meMember.id),
    );
  }

  /**
   * Build initial participants for create mode.
   * Adds organiser only — Me state is now derived from calendar linkage,
   * not stored as a participant label.
   */
  static initializeCreateModeParticipants(
    options: CreateModeParticipantOptions,
  ): EventParticipant[] {
    const { ownerEmail, ownerName, userId, avatarUrl, calendarId } = options;

    let participants: EventParticipant[] = [];

    // Add organiser based on calendar owner
    if (ownerEmail) {
      participants = EventParticipantsManager.addOrganiser(
        participants,
        ownerEmail === undefined ? `calendar-${calendarId ?? 'unknown'}` : (userId ?? `calendar-${calendarId ?? 'unknown'}`),
        ownerName ?? undefined,
        ownerEmail,
        avatarUrl,
      );
    }

    return participants;
  }

  /**
   * Enrich edit-mode participants by syncing labels from existing invites.
   * If a calendar-linked member is in the invite list, ensure they have a label too.
   */
  static enrichEditModeParticipants(
    options: EditModeParticipantOptions,
  ): EventParticipant[] {
    const { participants, familyMembers, calendarMappings, calendars, meMemberId } = options;

    // Strip any existing Me labels — Me state is now derived from calendar linkage + metoggle
    let filtered = participants;
    if (meMemberId) {
      filtered = participants.filter(
        p => !(p.role === 'label' && p.familyMemberId === meMemberId),
      );
    }

    const memberCalendarEmails = FamilyMemberCalendarResolver.getCalendarEmailsForMembers(
      calendarMappings,
      calendars,
    );

    return EventParticipantsManager.syncLabelsFromInvites(
      filtered,
      familyMembers,
      memberCalendarEmails,
    );
  }
}
